import React, { useState } from 'react';
import { useSeo } from '../hooks/useSeo';

export default function ContactPage() {
  useSeo({
    title: 'Kontakt',
    description: 'Kontakt redaktionen på Skatunderlup. Tip redaktionen, send pressemeddelelser eller henvend dig med spørgsmål om skat og moms.',
  });

  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    description: '',
    website_url: '', // Honeypot field (mandatory, must remain empty)
  });

  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [serverError, setServerError] = useState('');

  const validate = () => {
    const errs = {};
    if (!formData.name.trim()) {
      errs.name = 'Venligst angiv dit navn';
    }
    if (!formData.phone.trim()) {
      errs.phone = 'Venligst angiv dit telefonnummer';
    } else if (!/^[+0-9\s-]{6,16}$/.test(formData.phone.trim())) {
      errs.phone = 'Ugyldigt telefonnummer';
    }
    if (!formData.email.trim()) {
      errs.email = 'Venligst angiv din e-mail';
    } else if (!/\S+@\S+\.\S+/.test(formData.email.trim())) {
      errs.email = 'Ugyldig e-mailadresse';
    }
    if (!formData.description.trim()) {
      errs.description = 'Venligst skriv en beskrivelse eller henvendelse';
    }
    return errs;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setServerError('');
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setErrors({});
    setSubmitting(true);

    try {
      let response;
      try {
        response = await fetch('/_api/webform/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            description: formData.description,
            website_url: formData.website_url, // Honeypot
          }),
        });
      } catch {
        // Fallback to dev absolute endpoint if relative proxy is unavailable
        response = await fetch('https://api.dev.auxo.dk/webform/submit', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            name: formData.name,
            phone: formData.phone,
            email: formData.email,
            description: formData.description,
            website_url: formData.website_url, // Honeypot
          }),
        });
      }

      if (response.ok) {
        setSubmitted(true);
        setFormData({
          name: '',
          phone: '',
          email: '',
          description: '',
          website_url: '',
        });
      } else {
        const errorData = await response.json().catch(() => ({}));
        setServerError(errorData.message || 'Der opstod en fejl ved afsendelse. Prøv venligst igen senere.');
      }
    } catch {
      setServerError('Kunne ikke oprette forbindelse til serveren. Tjek din internetforbindelse eller prøv igen senere.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white pb-16">
      {/* Header */}
      <div className="border-b border-slate-200 bg-slate-50/70 py-10 px-4">
        <div className="mx-auto max-w-7xl">
          <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#fd7302]">
            <i className="fa-solid fa-envelope-open-text" aria-hidden="true" />
            <span>Redaktionen</span>
          </div>
          <h1 className="mt-1 font-serif text-3xl sm:text-4xl lg:text-5xl font-black text-[#010b27]">
            Kontakt Skatunderlup
          </h1>
          <p className="mt-2 max-w-2xl text-sm sm:text-base text-slate-600 leading-relaxed">
            Har du et tip om en verserende sag, en pressemeddelelse eller spørgsmål til vores redaktionelle dækning? Skriv eller ring til os.
          </p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 pt-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
          {/* Left Column: Contact Cards */}
          <div className="lg:col-span-5 space-y-6">
            <div className="rounded-md border border-slate-200 bg-white p-6 shadow-xs">
              <h2 className="font-serif text-xl font-bold text-[#010b27] mb-4 pb-2 border-b border-slate-100">
                Redaktionelle oplysninger
              </h2>
              <dl className="space-y-4 text-xs sm:text-sm text-slate-700">
                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Medie
                  </dt>
                  <dd className="font-serif font-bold text-[#010b27] text-base">
                    Skatunderlup
                  </dd>
                </div>

                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Redaktionschef
                  </dt>
                  <dd className="font-semibold text-[#010b27]">
                    Pernille Thejlade
                  </dd>
                </div>

                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Adresse
                  </dt>
                  <dd className="not-italic">
                    128 City Road, 0000 London
                  </dd>
                </div>

                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    Telefon
                  </dt>
                  <dd>
                    <a
                      href="tel:50364043"
                      className="inline-block whitespace-nowrap font-semibold text-[#010b27] hover:text-[#fd7302] transition-colors"
                    >
                      <i className="fa-solid fa-phone mr-1.5 text-xs text-[#fd7302]" aria-hidden="true" />
                      <span className="whitespace-nowrap">50 36 40 43</span>
                    </a>
                  </dd>
                </div>

                <div>
                  <dt className="text-[11px] font-bold uppercase tracking-wider text-slate-500">
                    E-mail
                  </dt>
                  <dd>
                    <a
                      href="mailto:pnt@lexnovo.dk"
                      className="inline-block whitespace-nowrap font-semibold text-[#010b27] hover:text-[#fd7302] transition-colors"
                    >
                      <i className="fa-solid fa-envelope mr-1.5 text-xs text-[#fd7302]" aria-hidden="true" />
                      <span className="whitespace-nowrap">pnt@lexnovo.dk</span>
                    </a>
                  </dd>
                </div>
              </dl>
            </div>

            {/* Tip the editorial office card */}
            <div className="rounded-md border border-slate-200 bg-slate-50/70 p-6 text-xs text-slate-600 space-y-2">
              <span className="text-[10px] font-bold uppercase tracking-widest text-[#fd7302] block">
                Fortrolighed
              </span>
              <h3 className="font-serif text-base font-bold text-[#010b27]">
                Tip os anonymt eller fortroligt
              </h3>
              <p className="leading-relaxed">
                Kilders anonymitet er beskyttet i henhold til gældende presseetiske regler og medieansvarsloven. Angiv venligst, hvis din henvendelse ønskes behandlet under kildebeskyttelse.
              </p>
            </div>
          </div>

          {/* Right Column: Webform */}
          <div id="kontaktformular" className="lg:col-span-7 scroll-mt-8">
            <div className="rounded-md border border-slate-200 bg-white p-6 sm:p-8 shadow-xs">
              <h2 className="font-serif text-2xl font-bold text-[#010b27]">
                Uforpligtende initiel kontakt og vurdering
              </h2>
              <p className="mt-1 text-xs sm:text-sm text-slate-600 leading-relaxed">
                Ring til Martin eller skriv til ham ved at udfylde kontaktformular nedenfor og tryk på send.
              </p>

              {submitted ? (
                <div className="mt-6 rounded-md border border-green-600 bg-green-50 p-6 text-green-950">
                  <div className="flex items-center gap-3">
                    <i className="fa-solid fa-circle-check text-2xl text-green-700" aria-hidden="true" />
                    <h3 className="font-serif text-lg font-bold">
                      Tak for din henvendelse
                    </h3>
                  </div>
                  <p className="mt-2 text-xs sm:text-sm text-green-900 leading-relaxed">
                    Din besked er modtaget af redaktionen på Skatunderlup. Vi gennemgår alle henvendelser og kontakter dig ved behov for opfølgning.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSubmitted(false)}
                    className="mt-4 inline-flex items-center gap-2 rounded-sm border border-green-800 bg-white px-4 py-2 text-xs font-semibold text-green-950 hover:bg-green-100"
                  >
                    <span>Send endnu en besked</span>
                  </button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="mt-6 space-y-4" noValidate>
                  {serverError && (
                    <div className="rounded-sm border border-red-600 bg-red-50 p-4 text-xs text-red-900">
                      <i className="fa-solid fa-triangle-exclamation mr-1.5" aria-hidden="true" />
                      {serverError}
                    </div>
                  )}

                  {/* MANDATORY HONEYPOT ANTISPAM: Hidden from humans via sr-only + absolute */}
                  <div className="sr-only absolute -left-[9999px] top-auto h-0 w-0 overflow-hidden" aria-hidden="true">
                    <label htmlFor="website_url">Website URL (leave blank)</label>
                    <input
                      type="text"
                      id="website_url"
                      name="website_url"
                      value={formData.website_url}
                      onChange={(e) => setFormData({ ...formData, website_url: e.target.value })}
                      tabIndex={-1}
                      autoComplete="off"
                    />
                  </div>

                  {/* Navn */}
                  <div>
                    <label htmlFor="name" className="block text-xs font-bold uppercase tracking-wider text-[#010b27] mb-1">
                      Navn <span className="text-[#fd7302]">*</span>
                    </label>
                    <input
                      id="name"
                      type="text"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      placeholder="Dit fulde navn"
                      className={`w-full rounded-sm border bg-white px-3 py-2.5 text-xs sm:text-sm text-[#010b27] placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                        errors.name ? 'border-red-600 focus:ring-red-600' : 'border-slate-200 focus:border-[#fd7302] focus:ring-[#fd7302]'
                      }`}
                    />
                    {errors.name && (
                      <p className="mt-1 text-[11px] text-red-700 font-medium">
                        {errors.name}
                      </p>
                    )}
                  </div>

                  {/* Telefonnummer */}
                  <div>
                    <label htmlFor="phone" className="block text-xs font-bold uppercase tracking-wider text-[#010b27] mb-1">
                      Telefonnummer <span className="text-[#fd7302]">*</span>
                    </label>
                    <input
                      id="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                      placeholder="F.eks. 61 44 46 04"
                      className={`w-full rounded-sm border bg-white px-3 py-2.5 text-xs sm:text-sm text-[#010b27] placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                        errors.phone ? 'border-red-600 focus:ring-red-600' : 'border-slate-200 focus:border-[#fd7302] focus:ring-[#fd7302]'
                      }`}
                    />
                    {errors.phone && (
                      <p className="mt-1 text-[11px] text-red-700 font-medium">
                        {errors.phone}
                      </p>
                    )}
                  </div>

                  {/* Email */}
                  <div>
                    <label htmlFor="email" className="block text-xs font-bold uppercase tracking-wider text-[#010b27] mb-1">
                      E-mailadresse <span className="text-[#fd7302]">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                      placeholder="navn@virksomhed.dk"
                      className={`w-full rounded-sm border bg-white px-3 py-2.5 text-xs sm:text-sm text-[#010b27] placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                        errors.email ? 'border-red-600 focus:ring-red-600' : 'border-slate-200 focus:border-[#fd7302] focus:ring-[#fd7302]'
                      }`}
                    />
                    {errors.email && (
                      <p className="mt-1 text-[11px] text-red-700 font-medium">
                        {errors.email}
                      </p>
                    )}
                  </div>

                  {/* Beskrivelse */}
                  <div>
                    <label htmlFor="description" className="block text-xs font-bold uppercase tracking-wider text-[#010b27] mb-1">
                      Beskrivelse / Henvendelse <span className="text-[#fd7302]">*</span>
                    </label>
                    <textarea
                      id="description"
                      rows={5}
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Beskriv din henvendelse, dit tip eller hvad sagen drejer sig om..."
                      className={`w-full rounded-sm border bg-white px-3 py-2.5 text-xs sm:text-sm text-[#010b27] placeholder:text-slate-400 focus:outline-none focus:ring-1 ${
                        errors.description ? 'border-red-600 focus:ring-red-600' : 'border-slate-200 focus:border-[#fd7302] focus:ring-[#fd7302]'
                      }`}
                    />
                    {errors.description && (
                      <p className="mt-1 text-[11px] text-red-700 font-medium">
                        {errors.description}
                      </p>
                    )}
                  </div>

                  <div className="pt-2">
                    <button
                      type="submit"
                      disabled={submitting}
                      className="w-full sm:w-auto inline-flex items-center justify-center gap-2 rounded-sm bg-[#fd7302] px-7 py-3 text-xs sm:text-sm font-bold uppercase tracking-wider text-white hover:bg-[#e06500] transition-colors disabled:opacity-60 focus:outline-none focus:ring-2 focus:ring-[#fd7302]"
                    >
                      {submitting ? (
                        <>
                          <i className="fa-solid fa-spinner fa-spin" aria-hidden="true" />
                          <span>Sender...</span>
                        </>
                      ) : (
                        <>
                          <span>Send henvendelse</span>
                          <i className="fa-solid fa-paper-plane" aria-hidden="true" />
                        </>
                      )}
                    </button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
